import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { VoiceProfileDto } from '@app/shared';
import { useT } from '../i18n';
import { haptic } from '../lib/tma';
import { voiceApi } from '../lib/voice-client';
import { AudioPlayer } from './AudioPlayer';
import { BottomSheet, Button, Card, Skeleton, SkeletonGroup, Toast } from './ui';

const linkButton =
  'flex items-center justify-center h-10 w-full rounded-full bg-ink text-ink-on text-sm font-semibold transition-transform active:scale-[0.97]';

/**
 * «Образец голоса» on the settings screen (spec 6.2): record, listen and
 * confirm, record again, delete (spec 8.1, step 5). Stage 2 owns the screen.
 */
export function VoiceSettingsCard() {
  const t = useT();
  const qc = useQueryClient();
  const [asking, setAsking] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const voice = useQuery({
    queryKey: ['voice', 'profile'],
    queryFn: () => voiceApi.getVoiceProfile(),
  });

  const flash = (text: string) => {
    setToast(text);
    setTimeout(() => setToast(null), 2500);
  };

  const remove = useMutation({
    mutationFn: () => voiceApi.deleteVoice(),
    onSuccess: (res) => {
      setAsking(false);
      if (res.kind === 'ok') {
        qc.setQueryData<VoiceProfileDto>(['voice', 'profile'], { status: 'none', createdAt: null });
        haptic('success');
        flash(t.voiceSettings.deleted);
      } else {
        flash(t.voiceSettings.deleteFailed);
      }
    },
    onError: () => {
      setAsking(false);
      flash(t.common.failed);
    },
  });

  const status = voice.data?.status;

  return (
    <>
      <h2 className="text-xs font-bold uppercase tracking-[0.06em] text-hint mb-2">
        {t.voiceSettings.title}
      </h2>
      <Card variant="surface" padding="md" className="mb-5 space-y-3">
        {voice.isLoading && (
          <SkeletonGroup label={t.common.loading} className="space-y-3">
            <Skeleton shape="text" className="w-3/4" />
            <Skeleton className="h-10 rounded-full" />
          </SkeletonGroup>
        )}
        {voice.isError && <p className="text-sm text-hint">{t.common.failed}</p>}
        {status === 'none' && (
          <>
            <p className="text-sm">{t.voiceSettings.none}</p>
            <Link to="/voice/record?back=/settings" className={linkButton}>
              {t.voiceSettings.record}
            </Link>
          </>
        )}
        {status === 'pending' && (
          <>
            <p className="text-sm">{t.voiceSettings.pending}</p>
            <Link to="/voice/record?step=preview&back=/settings" className={linkButton}>
              {t.voiceSettings.listen}
            </Link>
          </>
        )}
        {status === 'ready' && (
          <>
            <p className="text-sm">{t.voiceSettings.ready}</p>
            <AudioPlayer
              queryKey={['voice', 'preview', 'settings']}
              load={() => voiceApi.getVoicePreview()}
            />
            <Link
              to="/voice/record?back=/settings"
              className="block text-center text-sm font-semibold text-fg py-1"
            >
              {t.voiceSettings.rerecord}
            </Link>
            <Button
              variant="ghost"
              fullWidth
              className="text-destructive"
              onClick={() => setAsking(true)}
            >
              {t.voiceSettings.delete}
            </Button>
          </>
        )}
      </Card>

      <BottomSheet open={asking} onClose={() => setAsking(false)} title={t.voiceSettings.delete}>
        <p className="text-sm mb-4">{t.voiceSettings.deleteConfirm}</p>
        <div className="flex gap-2">
          <Button variant="secondary" fullWidth onClick={() => setAsking(false)}>
            {t.voiceSettings.cancel}
          </Button>
          <Button
            variant="destructive"
            fullWidth
            loading={remove.isPending}
            onClick={() => remove.mutate()}
          >
            {t.voiceSettings.delete}
          </Button>
        </div>
      </BottomSheet>

      {toast && (
        <Toast tone="neutral" placement="fixed">
          {toast}
        </Toast>
      )}
    </>
  );
}
